import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortModel,
} from '@mui/x-data-grid-premium';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  ExtendedGridRowSelectionModel,
  normalizeRowSelectionModel,
} from '@/shared/lib/rowSelectionUtils';

export type ColumnDef = GridColDef & {
  customFieldType?: string;
};

export interface ViewState {
  pageId: string;
  viewId: string;
  viewName: string;
  viewType: 'grid' | 'kanban';
  position: number;
  isGeneral: boolean;
  groupByColumn: string | undefined;

  columns: ColumnDef[];
  filterModel: GridFilterModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;

  tables: {
    groupByValue?: string;

    columnOrder: string[];
    pinnedColumns: GridPinnedColumnFields;
    rowSelectionModel: ExtendedGridRowSelectionModel;
  }[];
}

export const EMPTY_STATE_TABLE: ViewState = {
  pageId: '',
  viewId: '',
  viewName: 'Grid View',
  viewType: 'grid',
  position: 0,
  isGeneral: false,
  groupByColumn: undefined,

  columns: [],
  paginationModel: {
    page: 0,
    pageSize: 20,
  },
  filterModel: { items: [] },
  sortModel: [],
  columnVisibilityModel: {},

  tables: [
    {
      groupByValue: undefined,

      columnOrder: [],
      pinnedColumns: {
        left: [GRID_CHECKBOX_SELECTION_FIELD],
        right: [],
      },

      rowSelectionModel: {
        ids: [],
        rowLookup: {},
      },
    },
  ],
};

export interface DataGridState {
  activeViews: Record<string, string>;
  views: ViewState[];

  updateStateFromApi: (views: ViewState[]) => void;

  setGroupBy: (
    pageId: string,
    groupByColumn: string | undefined,
    groupByOptions?: { label: string; value: string }[],
  ) => void;

  addView: (view: ViewState) => void;
  updateViewName: (viewId: string, viewName: string) => void;
  setActiveView: (pageId: string, activeView: string) => void;
  deleteView: (viewId: string) => void;

  setColumns: (pageId: string, columns: ColumnDef[]) => void;
  setFilterModel: (pageId: string, filters: GridFilterModel) => void;
  setPaginationModel: (pageId: string, pagination: GridPaginationModel) => void;
  setColumnVisibilityModel: (pageId: string, columns: GridColumnVisibilityModel) => void;

  setColumnOrder: (pageId: string, groupByValue: string | undefined, columnOrder: string[]) => void;
  setPinnedColumns: (
    pageId: string,
    groupByValue: string | undefined,
    pinnedColumns: GridPinnedColumnFields,
  ) => void;
  setSortModel: (pageId: string, sorting: GridSortModel) => void;
  setRowSelectionModel: (
    pageId: string,
    groupByValue: string | undefined,
    rowSelection: ExtendedGridRowSelectionModel,
  ) => void;

  modifiedViews: ViewState[];
  removeModifiedView: (view: ViewState) => void;
  removeAllModifiedViews: () => void;

  setViewsOrder: (viewIds: string[]) => void;

  clearState: () => void;
}

const useDataGridStore = create<DataGridState>()(
  devtools(
    (set) => {
      return {
        activeViews: {} as Record<string, string>,
        views: [] as ViewState[],
        modifiedViews: [] as ViewState[],

        updateStateFromApi: (views: ViewState[]) =>
          set(
            (state: DataGridState) => {
              const normalizedViews =
                views?.map((view) => ({
                  ...view,
                  tables:
                    view.tables?.map((table) => ({
                      ...table,
                      rowSelectionModel: normalizeRowSelectionModel(table.rowSelectionModel),
                    })) ?? [],
                })) ?? [];

              return {
                views: normalizedViews,
                activeViews: {
                  [normalizedViews[0].pageId]:
                    state.activeViews?.[normalizedViews[0].pageId] ?? normalizedViews[0].viewId,
                },
              };
            },
            undefined,
            'updateStateFromApi',
          ),

        addView: (view: ViewState) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const activeViews = { ...state.activeViews };

              const normalizedView = {
                ...view,
                tables: view.tables.map((table) => ({
                  ...table,
                  rowSelectionModel: normalizeRowSelectionModel(table.rowSelectionModel),
                })),
              };

              const newViews = [...views, normalizedView];

              if (newViews.length === 1) {
                activeViews[normalizedView.pageId] = normalizedView.viewId;
              }

              return {
                activeViews,
                views: newViews,
                modifiedViews: [...modifiedViews, normalizedView],
              };
            },
            undefined,
            'addView',
          ),

        setActiveView: (pageId: string, activeView: string) =>
          set(
            (state: DataGridState) => {
              const activeViews = { ...state.activeViews };
              activeViews[pageId] = activeView;

              return {
                activeViews,
              };
            },
            undefined,
            'setActiveView',
          ),

        updateViewName: (viewId: string, viewName: string) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];

              const viewIndex = views.findIndex((v) => v.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].viewName = viewName;
                modifiedViews.push(views[viewIndex]);
              }

              return {
                ...state,
                views,
                modifiedViews,
              };
            },
            undefined,
            'updateViewName',
          ),

        deleteView: (viewId: string) =>
          set(
            (state: DataGridState) => {
              let views = [...state.views];
              const activeViews = { ...state.activeViews };

              const selectedView = views.find((t) => t.viewId === viewId);
              if (selectedView) {
                views = views.filter((t) => t.viewId !== viewId);

                if (activeViews[selectedView.pageId] === selectedView.viewId) {
                  activeViews[selectedView.pageId] = views?.[0]?.viewId ?? '';
                }
              }

              return {
                activeViews,
                views,
              };
            },
            undefined,
            'deleteView',
          ),

        setColumns: (pageId: string, columns: ColumnDef[]) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((v) => v.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].columns = columns;

                views[viewIndex].tables = views[viewIndex].tables.map((table) => ({
                  ...table,
                  columnOrder: views[viewIndex].columns.map((col) => col.field),
                }));

                modifiedViews.push(views[viewIndex]);
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setColumns',
          ),

        setColumnOrder: (pageId: string, groupByValue: string | undefined, columnOrder: string[]) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((v) => v.viewId === viewId);
              if (viewIndex !== -1) {
                const tablesToUpdate = views[viewIndex].tables.filter(
                  (t) => t.groupByValue === groupByValue,
                );
                const otherTables = views[viewIndex].tables.filter(
                  (t) => t.groupByValue !== groupByValue,
                );

                if (tablesToUpdate.length) {
                  views[viewIndex].tables = [
                    ...otherTables,
                    ...tablesToUpdate.map((table) => ({
                      ...table,
                      columnOrder,
                    })),
                  ];

                  modifiedViews.push(views[viewIndex]);
                }
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setColumnOrder',
          ),

        setPinnedColumns: (
          pageId: string,
          groupByValue: string | undefined,
          pinnedColumns: GridPinnedColumnFields,
        ) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((t) => t.viewId === viewId);
              if (viewIndex !== -1) {
                const tablesToUpdate = views[viewIndex].tables.filter(
                  (t) => t.groupByValue === groupByValue,
                );
                const otherTables = views[viewIndex].tables.filter(
                  (t) => t.groupByValue !== groupByValue,
                );

                if (tablesToUpdate.length) {
                  views[viewIndex].tables = [
                    ...otherTables,
                    ...tablesToUpdate.map((table) => ({
                      ...table,
                      pinnedColumns,
                    })),
                  ];

                  modifiedViews.push(views[viewIndex]);
                }
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setPinnedColumns',
          ),

        setPaginationModel: (pageId: string, paginationModel: GridPaginationModel) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((v) => v.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].paginationModel = paginationModel;

                modifiedViews.push(views[viewIndex]);
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setPaginationModel',
          ),

        setSortModel: (pageId: string, sortModel: GridSortModel) =>
          set(
            (state: DataGridState) => {
              const activeViews = { ...state.activeViews };
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((t) => t.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].sortModel = sortModel;

                modifiedViews.push(views[viewIndex]);
              }

              return {
                activeViews,
                views,
                modifiedViews,
              };
            },
            undefined,
            'setSortModel',
          ),

        setFilterModel: (pageId: string, filterModel: GridFilterModel) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((v) => v.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].filterModel = filterModel;

                modifiedViews.push(views[viewIndex]);
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setFilterModel',
          ),

        setRowSelectionModel: (
          pageId: string,
          groupByValue: string | undefined,
          rowSelectionModel: ExtendedGridRowSelectionModel,
        ) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((t) => t.viewId === viewId);
              if (viewIndex !== -1) {
                const tablesToUpdate = views[viewIndex].tables.filter(
                  (t) => t.groupByValue === groupByValue,
                );
                const otherTables = views[viewIndex].tables.filter(
                  (t) => t.groupByValue !== groupByValue,
                );

                if (tablesToUpdate.length) {
                  views[viewIndex].tables = [
                    ...otherTables,
                    ...tablesToUpdate.map((table) => ({
                      ...table,
                      rowSelectionModel: normalizeRowSelectionModel(rowSelectionModel),
                    })),
                  ];
                }
              }

              return {
                views,
              };
            },
            undefined,
            'setRowSelectionModel',
          ),

        setColumnVisibilityModel: (
          pageId: string,
          columnVisibilityModel: GridColumnVisibilityModel,
        ) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((t) => t.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].columnVisibilityModel = columnVisibilityModel;

                modifiedViews.push(views[viewIndex]);
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setColumnVisibilityModel',
          ),

        setGroupBy: (
          pageId: string,
          groupByColumn: string | undefined,
          groupByOptions?: { label: string; value: string }[],
        ) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];
              const modifiedViews = [...state.modifiedViews];
              const viewId = state.activeViews?.[pageId] ?? '';

              const viewIndex = views.findIndex((t) => t.viewId === viewId);
              if (viewIndex !== -1) {
                views[viewIndex].groupByColumn = groupByColumn;

                if (groupByColumn) {
                  views[viewIndex].tables = groupByOptions?.map((option) => ({
                    groupByValue: option.value,
                    columnOrder: views[viewIndex].columns.map((col) => col.field),
                    pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_FIELD], right: [] },
                    rowSelectionModel: { ids: [], rowLookup: [] },
                  })) ?? [
                    {
                      groupByValue: undefined,
                      columnOrder: views[viewIndex].columns.map((col) => col.field),
                      pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_FIELD], right: [] },
                      rowSelectionModel: { ids: [], rowLookup: [] },
                    },
                  ];
                } else {
                  views[viewIndex].tables = [
                    {
                      groupByValue: undefined,
                      columnOrder: views[viewIndex].columns.map((col) => col.field),
                      pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_FIELD], right: [] },
                      rowSelectionModel: { ids: [], rowLookup: [] },
                    },
                  ];
                }

                modifiedViews.push(views[viewIndex]);
              }

              return {
                views,
                modifiedViews,
              };
            },
            undefined,
            'setGroupBy',
          ),

        setViewsOrder: (viewIds: string[]) =>
          set(
            (state: DataGridState) => {
              const views = [...state.views];

              const viewsToReorder = viewIds
                .map((id) => views.find((view) => view.viewId === id)!)
                .filter(Boolean);
              const otherViews = views.filter((view) => !viewIds.includes(view.viewId));

              const updatedViews = [...viewsToReorder, ...otherViews];

              return {
                views: updatedViews.map((view, index) => ({
                  ...view,
                  position: index,
                })),
              };
            },
            undefined,
            'setViewsOrder',
          ),

        removeModifiedView: (view: ViewState) =>
          set(
            (state: DataGridState) => {
              const modifiedViews = [...state.modifiedViews];
              return {
                modifiedViews: modifiedViews.filter((t) => t.viewId !== view.viewId),
              };
            },
            undefined,
            'removeModifiedView',
          ),

        removeAllModifiedViews: () =>
          set(
            () => {
              return {
                modifiedViews: [],
              };
            },
            undefined,
            'removeAllModifiedViews',
          ),

        clearState: () =>
          set(
            () => {
              return { views: [], modifiedViews: [], activeViews: {} as Record<string, string> };
            },
            undefined,
            'clearState',
          ),
      };
    },
    {
      enabled: true,
    },
  ),
);

export default useDataGridStore;
